import {
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  SelectionMode,
  Shimmer,
  ShimmerElementType,
  Stack,
  Text,
} from '@fluentui/react';
import { useMemo } from 'react';

import { TableDataRow } from '../types';
import { UnitType } from './TimeSeriesStacked';

interface UsageDataTableProps {
  rows: TableDataRow[];
  timeColumns: string[];
  firstColumnHeader: string;
  isLoading: boolean;
  groupColors?: Record<string, string>;
  unitType?: UnitType;
}

// Column widths
const FIRST_COLUMN_WIDTH = 180;
const TIME_COLUMN_WIDTH = 90;

/**
 * Format a value for display in the table.
 * For tokens, abbreviate large numbers (e.g., 1.5M, 500K).
 * For AI Units, keep the original format.
 */
function formatDisplayValue(value: string | number, unitType: UnitType = 'ai_units'): string {
  if (value === '-') return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);

  if (unitType === 'tokens') {
    if (num >= 1_000_000) {
      const millions = num / 1_000_000;
      return millions % 1 === 0 ? `${millions}M` : `${millions.toFixed(1)}M`;
    }
    if (num >= 1_000) {
      const thousands = num / 1_000;
      return thousands % 1 === 0 ? `${thousands}K` : `${thousands.toFixed(1)}K`;
    }
    return num.toFixed(0);
  }
  return String(value);
}

/**
 * UsageDataTable - Displays usage data in a table format with sticky first column
 *
 * Features:
 * - First column (group names) is sticky during horizontal scroll
 * - Time columns are dynamically generated from data
 * - Loading state shows shimmer placeholders
 * - Empty state shows centered message
 */
export function UsageDataTable({
  rows,
  timeColumns,
  firstColumnHeader,
  isLoading,
  groupColors,
  unitType = 'ai_units',
}: UsageDataTableProps) {
  // Generate columns dynamically
  const columns: IColumn[] = useMemo(() => {
    // First column for group names (sticky)
    const groupColumn: IColumn = {
      key: 'group',
      name: firstColumnHeader,
      fieldName: 'group',
      minWidth: FIRST_COLUMN_WIDTH,
      maxWidth: FIRST_COLUMN_WIDTH,
      isResizable: false,
      styles: {
        root: {
          position: 'sticky',
          left: 0,
          backgroundColor: 'white',
          zIndex: 1,
        },
      },
      onRender: (item: TableDataRow) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {groupColors && groupColors[item.group] && (
            <div
              style={{
                width: 14,
                height: 14,
                backgroundColor: groupColors[item.group],
                borderRadius: 2,
                flexShrink: 0,
              }}
            />
          )}
          <Text
            styles={{
              root: {
                fontWeight: 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
            title={item.group}
          >
            {item.group}
          </Text>
        </div>
      ),
    };

    // Time columns
    const timeColumnsList: IColumn[] = timeColumns.map((time) => ({
      key: time,
      name: time,
      fieldName: time,
      minWidth: TIME_COLUMN_WIDTH,
      maxWidth: TIME_COLUMN_WIDTH,
      isResizable: false,
      styles: {
        root: {
          textAlign: 'right',
        },
      },
      onRender: (item: TableDataRow) => (
        <Text
          styles={{
            root: {
              display: 'block',
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
            },
          }}
        >
          {formatDisplayValue(item[time], unitType)}
        </Text>
      ),
    }));

    return [groupColumn, ...timeColumnsList];
  }, [firstColumnHeader, timeColumns, groupColors, unitType]);

  // Loading state - show shimmer rows
  if (isLoading) {
    const shimmerRows = Array.from({ length: 5 }, (_, i) => ({ key: `shimmer-${i}` }));
    return (
      <div style={{ overflowX: 'auto' }}>
        <Stack tokens={{ childrenGap: 8 }} styles={{ root: { padding: '8px 0' } }}>
          {/* Header shimmer */}
          <Shimmer shimmerElements={[{ type: ShimmerElementType.line, height: 20, width: '100%' }]} />
          {/* Row shimmers */}
          {shimmerRows.map((row) => (
            <Shimmer key={row.key} shimmerElements={[{ type: ShimmerElementType.line, height: 32, width: '100%' }]} />
          ))}
        </Stack>
      </div>
    );
  }

  // Empty state
  if (rows.length === 0) {
    return (
      <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { minHeight: 200, color: '#605e5c' } }}>
        <Text variant="medium">No data available</Text>
      </Stack>
    );
  }

  return (
    <div
      style={{
        overflowX: 'auto',
        position: 'relative',
      }}
    >
      <DetailsList
        items={rows}
        columns={columns}
        selectionMode={SelectionMode.none}
        layoutMode={DetailsListLayoutMode.fixedColumns}
        isHeaderVisible={true}
        styles={{
          root: {
            // Remove default header gray background
            '.ms-DetailsHeader': {
              backgroundColor: 'white',
              borderBottom: '1px solid #edebe9',
              paddingTop: 0,
            },
            '.ms-DetailsHeader-cell': {
              backgroundColor: 'white',
            },
            '.ms-DetailsHeader-cellName': {
              fontWeight: 600,
            },
            // Ensure first column cells are sticky
            '.ms-DetailsRow-cell:first-child': {
              position: 'sticky',
              left: 0,
              backgroundColor: 'white',
              zIndex: 1,
            },
            // Sticky header for first column
            '.ms-DetailsHeader-cell:first-child': {
              position: 'sticky',
              left: 0,
              backgroundColor: 'white',
              zIndex: 2,
            },
          },
          headerWrapper: {
            position: 'sticky',
            top: 0,
            zIndex: 3,
          },
        }}
      />
    </div>
  );
}
